import classNames from 'classnames';
import FavouriteSpace from 'components/favouriteSpace';
import Favourites from 'components/favourites';
import FavouriteVenue from 'components/favouriteVenue';
import VenueSearchForm from 'components/forms/venueSearch';
import Page from 'components/page';
import Pager from 'components/pager';
import SearchMap from 'components/searchMap';
import MapVenue from 'components/searchMap/mapVenue';
import SortDropDown, { SortOption } from 'components/sortDropDown';
import Switch from 'components/switch';
import { sortFields } from 'constants/sortFields';
import useIsMobile from 'hooks/useIsMobile';
import useStoredBooking from 'hooks/useStoredBooking';
import { Venue } from 'hooks/useVenue';
import useVenueSearch, { Filters } from 'hooks/useVenueSearch';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { AuthContext } from 'state/auth';
import { FavouritesContext } from 'state/favourites';
import { initialState as emptyState } from 'state/search/reducer';
import { useTranslation } from 'utils/i18n';
import { serverSideTranslations } from '../../lib/serverSideTranslations';

type View = 'list' | 'map';

interface Props {
  pageSize?: number;
  initialPage?: number;
  initialView?: View;
  initialFilters?: Filters;
}

export const defaultFilters: Filters = {
  distance: null as unknown as number,
  sort: 'Name' as unknown as Filters['sort'],
  location: '',
  latLng: null as unknown as Filters['latLng'],
};

const PropertiesPage: NextPage<Props> = ({
  pageSize = 10,
  initialPage = 1,
  initialView = 'map',
  initialFilters = defaultFilters,
}) => {
  const { t } = useTranslation('common');
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [currentView, setCurrentView] = useState<View>(initialView);

  const [filters, setFilters] = useState<Filters>(initialFilters);
  const { location, latLng, distance = '', sort = '' } = filters;

  const { loggedIn } = useContext(AuthContext);
  const {
    actions: {
      fetchFavourites = (_type?: string) => {},
      removeFavourite = (_id?: number, _type?: string) => null,
    } = {},
    venues: favouriteVenues = [],
    spaces: favouriteSpaces = [],
  } = useContext(FavouritesContext);

  const displayLocation = latLng ? t('shared.current_location') : location;
  useEffect(() => {
    if (loggedIn) {
      fetchFavourites('venues');
      fetchFavourites('spaces');
    }
  }, [fetchFavourites, loggedIn]);

  const {
    data: {
      venues = [],
      alternativeVenues = [],
      totalVenues = 0,
      unfilteredTotalVenues = 0,
    } = {},
    loading: venuesLoading,
  } = useVenueSearch({
    filters,
    viewType: currentView,
    page: currentPage,
    perPage: pageSize,
  });

  const mapRef = useRef<{ resetMap: () => void }>(null);

  const [selectedVenue, setSelectedVenue] = useState<Venue | undefined>();

  const selectVenue = (venue?: Venue) => {
    setCurrentView('map');
    setSelectedVenue(venue);
  };

  const search = ({
    location: nextLocation,
    distance: nextDistance,
    geolocation,
  }: {
    location: string;
    distance?: string;
    geolocation?: { lat: number; lng: number } | null;
  }) => {
    setFilters(prev => ({
      ...prev,
      location: nextLocation,
      distance: nextDistance,
      latLng: geolocation,
    }));
    setCurrentPage(1);
  };

  const router = useRouter();

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (space: { id: number }, venue: { id?: number }) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue.id)
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...storedSpaceIds, space.id],
      }));

    router.push(`/rooms/${space.id}`);
  };

  const toggleView = (toggled: boolean) => {
    const nextView = toggled ? 'list' : 'map';
    setCurrentView(nextView);
    setCurrentPage(1);
    if (nextView !== 'map') setSelectedVenue(undefined);
  };

  const isMobile = useIsMobile();

  const searchResults = venues.length > 0 ? venues : alternativeVenues;

  const currentPageData: ReactNode = searchResults.map(venue => (
    <li key={venue.id}>
      <MapVenue
        className="venues__list--card"
        venue={venue}
        location={displayLocation || ''}
        showRooms
        showFeatures
        showBookRoomsButton
        showMapOnThumbNail
        floorplansButtonPosition="image"
        onClickMap={() => selectVenue(venue)}
        organisationName={
          (venue as Venue & { organisation: { name: string } }).organisation
            .name
        }
      />
    </li>
  ));

  const handlePageChange = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setCurrentPage(page => page + 1);
  };

  const formBlockClasses = classNames('block', 'block--with-border', {
    'block--no-margin': isMobile,
  });
  const classForControls = classNames('layout__first__top', {
    'layout__first__top--map-view': currentView === 'map',
  });
  const renderSearchForm = () => (
    <div className={formBlockClasses}>
      <div className="block__inner">
        <div className="block__title">
          <h3 className="block__title-heading">
            {t('venue_search_form.heading')}
          </h3>
        </div>
        <VenueSearchForm
          initialValues={{
            location: location || '',
            distance: (distance || '') as string,
            geolocation: latLng,
          }}
          onSubmit={search}
          onReset={mapRef.current?.resetMap}
          enableReset={!isMobile}
        />
      </div>
    </div>
  );

  const renderSubHeading = () => {
    if (currentView === 'map') {
      if (totalVenues === unfilteredTotalVenues)
        return <p>{t('showing_properties.showing_all_properties')}</p>;

      const ResetLink = ({
        children,
        ...props
      }: {
        children?: ReactNode;
        [key: string]: unknown;
      }) => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            setFilters(emptyState.filters as unknown as Filters);
          }}
          title={t('showing_properties.see_all_properties')}
          {...props}
        >
          {children}
        </a>
      );

      return (
        <p>
          {/*
            No `i18n` prop: Trans resolves the instance from the I18nextProvider
            that appWithTranslation renders. Passing one explicitly is what broke
            this string — the instance it was given held no resources, so the key
            rendered verbatim.
          */}
          <Trans
            i18nKey="showing_properties.total_properties_found"
            components={[<ResetLink key={'reset-link'} />]}
            count={totalVenues}
            values={{ total: unfilteredTotalVenues }}
          />
        </p>
      );
    }

    return (
      <p>
        {t(
          totalVenues > pageSize
            ? 'showing_properties.showing_properties_long'
            : 'showing_properties.showing_properties_short',
          { count: searchResults.length, total: totalVenues }
        )}
      </p>
    );
  };

  const sortOptions = Object.keys(sortFields) as SortOption[];
  const unavailableSortOptions = location === '' ? ['Distance'] : [];
  const availableSortOptions = sortOptions.filter(
    option => !unavailableSortOptions.includes(option as SortOption)
  );

  const changeSortBy = (sort: string) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, sort }));
  };
  const noResultsMessage = () => {
    if (!displayLocation) {
      return t('search_results.alternative_venues.no_matches');
    }

    if (alternativeVenues.length > 0) {
      return (
        <>
          {t('search_results.alternative_venues.no_location_results')}
          <strong>{displayLocation}</strong>.{' '}
          {t('search_results.alternative_venues.nearest_properties')}
        </>
      );
    }

    return (
      <>
        {t('search_results.alternative_venues.no_location_results')}
        <strong>{displayLocation}</strong>.{' '}
        {t('search_results.alternative_venues.check_spelling')}
      </>
    );
  };

  return (
    <Page title={t('headers.properties_page.title')}>
      <main className="venues">
        <section>
          <div className="section-content">
            <h3>{t('headers.properties_page.heading')}</h3>
            {isMobile && renderSearchForm()}
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                {!venuesLoading && venues.length === 0 && (
                  <div className="venue-search__alternative_venues_message">
                    <p>{noResultsMessage()}</p>
                  </div>
                )}
                <div className={classForControls}>
                  {!isMobile && renderSubHeading()}
                  <div className="layout__first__top--controls">
                    <Switch
                      className="venues__switch"
                      label={t('search_results.view_type.label')}
                      toggled={currentView === 'map'}
                      onSwitchChange={toggleView}
                    />

                    <SortDropDown
                      sortOptions={availableSortOptions}
                      onChange={e => changeSortBy(e.currentTarget.value)}
                      value={sort}
                      disabled={currentView === 'map'}
                    />
                  </div>
                </div>

                {currentView === 'map' && (
                  <>
                    <SearchMap
                      ref={mapRef}
                      venues={venues.length ? venues : alternativeVenues}
                      loading={venuesLoading}
                      selectedVenue={selectedVenue}
                      onSelectVenue={selectVenue}
                      className="our-properties-map"
                      location={displayLocation ?? undefined}
                      geolocation={latLng ?? undefined}
                      showRooms
                      showBookRoomsButton={!isMobile}
                      showFeatures={!isMobile}
                      floorplansButtonPosition={isMobile ? 'none' : 'image'}
                      imagePosition="top"
                    />
                    {isMobile && renderSubHeading()}
                  </>
                )}

                {currentView === 'list' && (
                  <>
                    {venuesLoading && (
                      <div className="search-map__container">
                        <i className="fa fa-spinner spinning" />
                      </div>
                    )}
                    {!venuesLoading && (
                      <ul data-testid="venue-list">{currentPageData}</ul>
                    )}
                    {!venuesLoading && searchResults.length > 0 && (
                      <Pager
                        className="venue-search--pager"
                        loading={venuesLoading}
                        currentPage={currentPage}
                        perPage={pageSize}
                        total={totalVenues}
                        onClick={handlePageChange}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="layout__last">
                {!isMobile && renderSearchForm()}

                {loggedIn && (
                  <Favourites
                    title={t('favourite.venues.title')}
                    placeholder={t('favourite.venues.placeholder')}
                    showFavourites={!!favouriteVenues.length}
                  >
                    {favouriteVenues.map(venue => (
                      <FavouriteVenue
                        key={venue.id}
                        venue={venue}
                        onRemove={() => removeFavourite(venue.id, 'venues')}
                      />
                    ))}
                  </Favourites>
                )}

                {loggedIn && (
                  <Favourites
                    title={t('favourite.spaces.title')}
                    placeholder={t('favourite.spaces.placeholder')}
                    showFavourites={!!favouriteSpaces.length}
                  >
                    {favouriteSpaces.map(space => (
                      <FavouriteSpace
                        key={space.id}
                        space={space}
                        selectSpace={selectSpace}
                        onRemove={() => removeFavourite(space.id, 'spaces')}
                        navigateToSpace
                      />
                    ))}
                  </Favourites>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

export const getServerSideProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default PropertiesPage;
